import useTranslation from 'next-translate/useTranslation'
import { FC } from 'react'
import { FlexboxGrid } from 'rsuite'

import { useAuth } from '../services/useAuth'
import { formatGenericIntegerNumber, formatPriceNumber } from '../utils/math'
import { getProductCurrency, getProductTotalPieceCountText } from '../lib/product'
import { DiscountProduct, Product, ProductQuantity } from '../utils/types/Product'
import { getSalesUnit, getTotalPieceCount, isPairProduct } from '../lib/salesUnit'
import useIsOnMobile from '../services/useIsOnMobile'
import { ScreenSize } from '../utils/constants'
import usePrice from '../services/usePrice'

import styles from '../styles/ProductQuantitySection.module.less'

interface ProductTotalQuantitySectionProps {
  product: Product
  productQuantity: ProductQuantity[]
  totalQuantity: number
  isMoqWarningActive?: boolean
  takeRateExcludeQuantity?: number
  passedDiscount?: DiscountProduct
}

const ProductTotalQuantitySection: FC<ProductTotalQuantitySectionProps> = (props) => {
  const {
    product,
    totalQuantity,
    isMoqWarningActive,
    takeRateExcludeQuantity,
    passedDiscount,
  } = props

  const { t } = useTranslation('products')
  const isOnTablet = useIsOnMobile(ScreenSize.lg)
  const { isVatIncluded } = useAuth()

  const currency = getProductCurrency(product)
  const unit = isPairProduct(product) ? 'pair' : 'piece'
  const isZeroProducts = (Number.isNaN(+totalQuantity)) || Number(totalQuantity) < 0
  const unitPieceCount = getTotalPieceCount(product)
  const totalPieceCount = formatGenericIntegerNumber(unitPieceCount * Number(totalQuantity))

  const {
    default: {
      price: defaultPrice,
      total: totalDefaultPrice,
    },
    final: {
      total: price,
    },
    discount: {
      current: currentDiscount,
    },
    moq,
  } = usePrice(product, totalQuantity, { excludeQuantity: takeRateExcludeQuantity })

  const discount = totalDefaultPrice - (Number(totalQuantity) * Number((currentDiscount)?.['price.value']))

  const renderDiscountText = () => {
    if (currentDiscount || passedDiscount) {
      return (
        <>
          <small className={styles['letter-spacing-1']}>{(` ${t('Bulk discounts')} -${formatPriceNumber(discount)} ${currency}`)}</small>
        </>
      )
    }
    return ''
  }
  const getProductSalesUnit = (count: number) => t(getSalesUnit(product), { count })

  return (
    <FlexboxGrid
      className={styles['variant-row']}
      justify="space-between"
    >
      <div className="flex-shrink-col-static margin-right-spacer-double">
        { !isOnTablet && (
        <div className="text-color-primary">
          <b>
            {renderDiscountText()}
          </b>
        </div>
        )}
        <div>
          {!!totalQuantity && Number(totalQuantity) > 0 && !!defaultPrice && (
            <div>
              <b data-testid="product-total-price">
                {`${t('products:Total')}: ${formatPriceNumber(price)} ${currency}`}
              </b>
            </div>
          )}
          {isZeroProducts && (
            <div>
              <b data-testid="product-total-price">
                {`${t('Total')}: 0,00 ${currency} `}
              </b>
              <small className="margin-left-spacer-half">
                {t('VAT')}
                &nbsp;0&nbsp;%
              </small>
            </div>
          )}
        </div>
      </div>
      <div
        className={`${isMoqWarningActive ? styles['under-moq-highlight'] : ''}
          ${styles['moq-warning']}
        `}
      >
        {isZeroProducts ? (
          <b>
            {t('Add {{ quantity }} to reach minimum',
              {
                count: moq,
                quantity: formatGenericIntegerNumber(moq),
              })}
          </b>
        )
          : (
            <b data-testid="product-total-quantity">
              {moq > Number(totalQuantity)
                ? t('Add {{ quantity }} to reach minimum',
                  {
                    count: moq - Number(totalQuantity),
                    quantity: formatGenericIntegerNumber(moq - Number(totalQuantity)),
                  })
                : (
                  <>
                    {formatGenericIntegerNumber(totalQuantity)}
                    {' '}
                    {getProductSalesUnit(Number(totalQuantity))}
                    {' '}
                    <span
                      data-testid="product-total-pieces"
                      className="product-total-pieces"
                    >
                      (
                      {totalPieceCount}
                      {' '}
                      {getProductTotalPieceCountText(Number(totalPieceCount), unit, t)}
                      )
                    </span>
                  </>
                )}
            </b>
          )}
      </div>
    </FlexboxGrid>
  )
}

export default ProductTotalQuantitySection
